/**
 * Cart upsell item. Form-free by design: a product form cannot legally nest
 * inside the cart form, so dropping it is what lets the upsell list render
 * between the cart line items and the checkout button.
 */

if (!customElements.get('cart-upsell')) {
	class CartUpsell extends HTMLElement {
		constructor() {
			super();
			this.addButton = this.querySelector('[data-upsell-add]');
			this.select = this.querySelector('.cart-block-upsells__variant');
			this.priceElement = this.querySelector('.cart-block-upsells__price');
			this.messageEl = this.querySelector('.cart-block-upsells__message');
			this.busy = false;
			if (this.addButton) {
				this.addButton.addEventListener('click', this.add.bind(this));
			}
			if (this.select) {
				this.select.addEventListener('change', this.showSelectedVariantPrice.bind(this));
			}
		}

		// Variants of one product can differ in price, so the card must not keep advertising the first variant's.
		showSelectedVariantPrice() {
			const price = this.select.selectedOptions[0]?.dataset.price;
			if (this.priceElement && price) {
				this.priceElement.textContent = price;
			}
		}

		getVariantId() {
			if (this.select && this.select.value) {
				return this.select.value;
			}
			return this.dataset.variantId;
		}

		setBusy(isBusy) {
			this.busy = isBusy;
			if (this.addButton) {
				this.addButton.disabled = isBusy;
			}
		}

		// Visible echo as well as the announcement: a failure that only reaches the live region reads as a broken cart.
		showMessage(text) {
			if (this.messageEl) {
				this.messageEl.textContent = text;
				this.messageEl.hidden = text === '';
			}
			if (text) {
				window.UFT?.CartStatusAnnouncer?.announce?.(text);
			}
		}

		add() {
			const variantId = this.getVariantId();
			if (!variantId || this.busy) {
				return;
			}
			// Before setBusy: disabling the focused button unfocuses it, leaving the re-render nothing to restore.
			const focusKey = CartItems.activeFocusKey();
			this.setBusy(true);
			this.showMessage('');
			window.UFT.Cart.addItems([{ id: variantId, quantity: 1 }])
				.then(({ ok, state }) => {
					if (!ok) {
						this.showMessage(window.UFT.Cart.errorMessage(state, 'Could not add the item.'));
						this.setBusy(false);
						return;
					}
					this.onAdded(focusKey);
				})
				.catch((error) => {
					console.error(error);
					this.showMessage('Could not add the item.');
					this.setBusy(false);
				});
		}

		// A successful re-render filters this now-in-cart product out and replaces this element, so busy dies with it.
		onAdded(focusKey) {
			const cartItems = this.closest('cart-items');
			// Captured before the re-render, and only this upsell's own drawer: on /cart it lives in the page cart.
			const drawer = this.closest('cart-drawer');
			if (cartItems) {
				// The add already landed, so a failed repaint leaves a cart missing the item the shopper just bought.
				cartItems.rerenderFromFetch(focusKey).catch(() => {
					this.setBusy(false);
					cartItems.recoverFromStaleCart();
				});
			}
			if (drawer && typeof drawer.open === 'function') {
				drawer.open();
			}
		}
	}
	customElements.define('cart-upsell', CartUpsell);
}
